import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mic, Users, Building, ArrowRight, CheckCircle } from 'lucide-react';

const SpeakingPage = () => {
  const topics = [
    {
      title: 'The Customer-First Revolution',
      description: 'How putting customers at the center transforms organizations and drives sustainable growth.',
      duration: '45-60 minutes'
    },
    {
      title: 'Servant Leadership in Action',
      description: 'Practical strategies for leading with empathy, humility, and a focus on serving others.',
      duration: '45-60 minutes'
    },
    {
      title: 'Building High-Performance Teams',
      description: 'Creating cultures where people thrive and deliver exceptional results.',
      duration: '60-90 minutes'
    },
    {
      title: 'Customer Experience Excellence',
      description: 'Designing experiences that turn customers into loyal advocates.',
      duration: '45-60 minutes'
    }
  ];

  const eventTypes = [
    {
      icon: <Building size={32} />,
      title: 'Corporate Events',
      description: 'Keynotes and workshops for annual meetings, leadership summits, and company gatherings.'
    },
    {
      icon: <Users size={32} />,
      title: 'Conferences',
      description: 'Industry conference keynotes and breakout sessions on leadership and customer experience.'
    },
    {
      icon: <Mic size={32} />,
      title: 'Custom Workshops',
      description: 'Half-day or full-day interactive workshops tailored to your organization\'s needs.'
    }
  ];

  const benefits = [
    'Engaging storytelling that inspires action',
    'Practical frameworks attendees can implement immediately',
    'Real-world examples from successful transformations',
    'Interactive elements to maximize engagement',
    'Customized content for your audience and goals',
    'Post-event resources and follow-up materials'
  ];

  return (
    <div data-testid="speaking-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="speaking-hero-title">
                Speaking Engagements
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground mb-8 leading-relaxed">
                Inspire your team with keynotes and workshops that challenge conventional thinking 
                and provide actionable strategies for building customer-first organizations.
              </p>
              <Link
                to="/contact"
                data-testid="hero-cta-button"
                className="inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-105 shadow-lg"
              >
                Book a Speaking Engagement <ArrowRight className="ml-2" size={20} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_growth-guide-21/artifacts/bk4nv0y6_Gemini_Generated_Image_wg5a5zwg5a5zwg5a.png"
                alt="Will Owen speaking at conference"
                className="w-full rounded-2xl shadow-2xl"
                data-testid="speaking-hero-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="section-padding bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" data-testid="event-types-title">
              Perfect For
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              I speak at various types of events, tailoring content to your audience.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {eventTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`event-type-${index}`}
                className="glass-card rounded-2xl p-8 text-center hover:-translate-y-2 transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-6 text-accent">
                  {type.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4">{type.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{type.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Speaking Topics */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" data-testid="topics-title">
              Popular Topics
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Engaging presentations that inspire and equip your audience.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {topics.map((topic, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`topic-card-${index}`}
                className="glass-card rounded-2xl p-8"
              >
                <h3 className="text-2xl font-bold mb-4">{topic.title}</h3>
                <p className="text-muted-foreground mb-4 leading-relaxed">{topic.description}</p>
                <p className="text-sm text-accent font-medium">{topic.duration}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="section-padding bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="benefits-title">
              What You Get
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                data-testid={`benefit-${index}`}
                className="flex items-start space-x-4"
              >
                <CheckCircle className="text-accent flex-shrink-0 mt-1" size={24} />
                <p className="text-lg">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-card rounded-2xl p-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="cta-title">
              Let's Create Something Memorable
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Ready to bring powerful insights to your next event? Let's discuss how I can help.
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact-button"
              className="inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-105 shadow-lg"
            >
              Get in Touch <ArrowRight className="ml-2" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SpeakingPage;